import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Checkbox, Typography, FormControlLabel } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Form, Field } from 'src/components/hook-form';

export const WEBHOOK_EVENTS = (t: any) => [
  { key: 'ticket.add', label: t('TICKET_CREATED') },
  { key: 'ticket.update', label: t('TICKET_UPDATED') },
  { key: 'ticket.delete', label: t('TICKET_DELETED') },

  { key: 'contact.add', label: t('CONTACT_CREATED') },
  { key: 'contact.update', label: t('CONTACT_UPDATED') },
  { key: 'contact.delete', label: t('CONTACT_DELETED') },

  { key: 'company.add', label: t('COMPANY_CREATED') },
  { key: 'company.update', label: t('COMPANY_UPDATED') },
  { key: 'company.delete', label: t('COMPANY_DELETED') },
];

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: any;
  handleSubmitData: (data: any) => void;
};

export function WebhookForm({ open, onClose, selectedUser, handleSubmitData }: Props) {
  const { t } = useTranslate('translation');

  const fldList = [
    { name: 'name', required: true },
    { name: 'webhookUrl', required: true },
    { name: 'events', required: true },
  ];

  const webhookSchema = validationSchema(fldList, t);

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      name: '',
      webhookUrl: '',
      events: [] as string[],
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (selectedUser) {
      reset({
        name: selectedUser.name || '',
        webhookUrl: selectedUser.url || '',
        events: selectedUser.events || [],
      });
    } else {
      reset({ name: '', webhookUrl: '', events: [] });
    }
  }, [selectedUser, reset]);

  const onSubmit = (data: any) => {
    handleSubmitData({ id: selectedUser?.id || '', ...data });
    onCloseHandler();
  };

  const onCloseHandler = () => {
    reset({ name: '', webhookUrl: '', events: [] });
    onClose();
  };

  const selectedEvents = watch('events');

  const handleToggleEvent = (eventKey: string) => {
    const newEvents = selectedEvents.includes(eventKey)
      ? selectedEvents.filter((e) => e !== eventKey)
      : [...selectedEvents, eventKey];
    setValue('events', newEvents);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseHandler}>
      <DialogTitle>{selectedUser ? t('EDIT_WEBHOOK') : t('ADD_NEW_WEBHOOK')}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('WEBHOOK_EVENTS_INFO')}
        </Typography>

        <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 2 }}>
            <Field.Text name="name" label={t('NAME')} placeholder="Example: CRM Webhook" required />
          </Box>

          <Field.Text
            name="webhookUrl"
            label={t('WEBHOOK_URL')}
            placeholder="Example: https://example.com/api/webhook"
            required
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {t('EVENTS')}
          </Typography>

          <Grid container spacing={1}>
            {WEBHOOK_EVENTS(t).map((event) => (
              <Grid item xs={12} sm={6} key={event.key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedEvents.includes(event.key)}
                      onChange={() => handleToggleEvent(event.key)}
                    />
                  }
                  label={event.label}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'end', my: 3 }}>
            <Button onClick={onCloseHandler}>{t('CANCEL')}</Button>

            <Button type="submit" variant="contained" sx={{ ml: 2 }}>
              {t('SAVE')}
            </Button>
          </Box>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
